import { useCallback, useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { SearchInput } from "../../../../components";
import { AssignmentPeopleContext, RolePermissions, SortingDirection } from "../../../../enums";
import { useContentAssignmentsPermissions } from "../../../../hooks/useContentAssignmentsPermissions";
import { bindAction } from "../../../../interfaces";
import { type GenericFiltersMap } from "../../../../utils/filterUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import ContentGroupsNoResults from "../../Common/Fields/ContentGroupsNoResults";
import {
  ColumnToParamMap,
  getGroupColumnOptions,
  PeopleAssignmentColumns,
} from "../../PeopleAssignments/Overview/helpers/getColumnOptions";
import { resetSelectedItems, selectedItemsChanged } from "../state/slices/GroupsSlice";
import { fetchGroupsAction } from "../state/thunks/GroupsThunk";

const GET_SCORM_GROUPS_DEFAULT_PARAMS = {
  skip: 0,
  top: 10,
  orderBy: PeopleAssignmentColumns.Name,
  sortingDirection: SortingDirection.Descending,
  appliedFilter: {},
};

interface GroupsOverviewOwnProps {
  scormId: number;
  customHeaderContent: React.ReactElement;
}

export type GroupsOverviewProps = GroupsOverviewOwnProps & PropsFromRedux;

export const GroupsOverview = ({
  scormId,
  customHeaderContent,
  items,
  isLoading,
  itemsCount,
  selectedIds,
  fetchGroups,
  resetSelectedItems,
  onSelectedItemChanged,
}: GroupsOverviewProps) => {
  const renderSearchInput = (): React.ReactElement => <SearchInput placeholder="Search for Groups..." disabled />;

  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.Group,
    RolePermissions.AssetsManage,
  );

  useEffect(
    () => () => {
      resetSelectedItems();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getGroupsHandler = useCallback(
    (
      skip: number = GET_SCORM_GROUPS_DEFAULT_PARAMS.skip,
      top: number = GET_SCORM_GROUPS_DEFAULT_PARAMS.top,
      orderBy: string = GET_SCORM_GROUPS_DEFAULT_PARAMS.orderBy.toLowerCase(),
      sortingDirection: SortingDirection = GET_SCORM_GROUPS_DEFAULT_PARAMS.sortingDirection,
      appliedFilter: GenericFiltersMap<string> = GET_SCORM_GROUPS_DEFAULT_PARAMS.appliedFilter,
    ) => {
      const sortingColumn = ColumnToParamMap[orderBy.toLowerCase() as Lowercase<PeopleAssignmentColumns>];
      fetchGroups({
        entityId: scormId,
        skip,
        top,
        sortingColumn,
        sortingDirection,
        filter: appliedFilter,
        term: "",
      });
    },
    [fetchGroups, scormId],
  );

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={() => (
        <GenericItemsView
          defaultSortingColumnName=""
          items={items}
          dataCount={itemsCount}
          isLoading={isLoading}
          selectedIds={selectedIds}
          onSelectedItemChanged={onSelectedItemChanged}
          columnOptions={getGroupColumnOptions()}
          applyFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          resetFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          fetchData={getGroupsHandler}
          renderSearch={renderSearchInput}
          customHeaderContent={customHeaderContent}
          isSelectDisabled={() => true}
          noResultsContent={<ContentGroupsNoResults />}
        />
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const edit = state.library.scorm.edit;
  return {
    items: edit.groups.items,
    isLoading: edit.groups.isLoading,
    itemsCount: edit.groups.itemsCount,
    selectedIds: edit.groups.selectedIds,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchGroups: bindAction(fetchGroupsAction, dispatch),
  onSelectedItemChanged: bindAction(selectedItemsChanged, dispatch),
  resetSelectedItems: bindAction(resetSelectedItems, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupsOverview);
export default ConnectedComponent;
